import { inject, Injectable } from '@angular/core';
import { catchError, first, map, Observable, of, switchMap, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Ticket } from '../../domain/model/ticket.model';
import { TicketRepository } from '../../domain/repositories/ticket.repository';
import { IApiTicketGetBySearchParams } from '../interfaces/ticket-api.interface';
import { TicketApiService } from '../services/ticket-api.service';
import { TicketCacheService } from '../services/ticket-cache.service';
import { TicketMergeService } from '../services/ticket-merge.service';
import { TicketMockService } from '../services/ticket-mock.service';
import { TicketSessionService } from '../services/ticket-session.service';
import { TicketStorageService } from '../services/ticket-storage.service';
import { Logger } from 'src/app/core/interfaces/Logger';

@Injectable({
  providedIn: 'root',
})
export class TicketRepositoryAdapter implements TicketRepository {

  protected readonly logger = inject(Logger);
  private readonly sessionService = inject(TicketSessionService);
  protected readonly storageService = inject(TicketStorageService);
  protected readonly cache = inject(TicketCacheService);
  private readonly mergeService = inject(TicketMergeService);
  protected readonly apiService = environment.mocksData.tickets
    ? inject(TicketMockService)
    : inject(TicketApiService);

  getLastTicket(): Observable<Ticket | null> {
    return this.getCachedOrSessionLastTicket();
  }

  saveLastTicket(ticket: Ticket): Observable<Ticket> {
    return this.sessionService.save(ticket).pipe(
      tap(() => this.cache.setLastTicket(ticket)),
      map(() => ticket)
    );
  }

  removeLastTicket(): Observable<void> {
    return this.sessionService.delete().pipe(
      tap(() => this.cache.setLastTicket(null)),
      map(() => undefined)
    );
  }

  getAllTickets(): Observable<Ticket[]> {
    return this.getCachedOrRemoteTickets();
  }

  getTicketById(id: string): Observable<Ticket | null> {
    return this.cache.getCurrentTickets().pipe(
      first(),
      switchMap((tickets) => {
        const found = tickets?.find((ticket) => ticket?.id === id);

        if (found) {
          return of(found);
        }

        return this.storageService
          .getById(id)
          .pipe(tap((ticket) => ticket && this.cache.setTicket(ticket)));
      })
    );
  }

  getTicketsByIds(ids: string[]): Observable<Ticket[]> {
    return this.apiService.getByIds(ids).pipe(
      switchMap((serverTickets) => this.mergeWithLocalTickets(serverTickets)),
      catchError(() => this.loadFromStorage())
    );
  }

  getTicketBySearch(
    params?: IApiTicketGetBySearchParams
  ): Observable<Ticket[]> {
    return this.apiService.getBySearch(params?.queryParams).pipe(
      switchMap((serverTickets) => this.mergeWithLocalTickets(serverTickets)),
      catchError(() => this.loadFromStorage())
    );
  }

  saveTicket(ticket: Ticket): Observable<Ticket> {
    return this.persistTicket(ticket).pipe(map(() => ticket));
  }

  clearAllTickets(): Observable<void> {
    return this.storageService.clear().pipe(
      tap(() => this.cache.clear()),
      map(() => undefined)
    );
  }

  private getCachedOrSessionLastTicket(): Observable<Ticket | null> {
    return this.cache
      .getLastTicket()
      .pipe(
        switchMap((lastTicket) =>
          lastTicket ? of(lastTicket) : this.loadFromSession()
        )
      );
  }

  private loadFromSession(): Observable<Ticket | null> {
    return this.sessionService.get().pipe(
      tap((ticket) => this.cache.setLastTicket(ticket)),
      map((ticket) => ticket ?? null)
    );
  }

  private getCachedOrRemoteTickets(): Observable<Ticket[]> {
    return this.cache
      .getCurrentTickets()
      .pipe(
        switchMap((tickets) =>
          tickets?.length ? of(tickets) : this.loadFromApiOrStorage()
        )
      );
  }

  private loadFromApiOrStorage(): Observable<Ticket[]> {
    return this.apiService.getAll().pipe(
      switchMap((serverTickets) => this.mergeWithLocalTickets(serverTickets)),
      catchError(() => this.loadFromStorage())
    );
  }

  protected mergeWithLocalTickets(
    serverTickets: Ticket[]
  ): Observable<Ticket[]> {
    return this.storageService.getAll().pipe(
      map((localTickets) =>
        this.mergeService.merge(serverTickets, localTickets)
      ),
      switchMap((mergedTickets) => this.persistAndReturn(mergedTickets))
    );
  }

  private persistAndReturn(tickets: Ticket[]): Observable<Ticket[]> {
    return this.storageService.saveAll(tickets).pipe(
      tap(() => this.cache.setAll(tickets)),
      map(() => tickets)
    );
  }

  protected loadFromStorage(): Observable<Ticket[]> {
    return this.storageService
      .getAll()
      .pipe(tap((tickets) => this.cache.setAll(tickets)));
  }

  private persistTicket(ticket: Ticket): Observable<void> {
    return this.storageService.save(ticket).pipe(
      tap(() => this.cache.setTicket(ticket)),
      map(() => undefined)
    );
  }
}
